import { signal } from "@preact/signals";
import { t } from "../i18n";
import { Icon } from "../icon";
import * as S from "../state";

export const KEYS: [string, string][] = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

/** what is typed; the app's key handler fills it too */
export const typed = signal("");
export const pressed = signal("");

export function flash(k: string): void {
  pressed.value = k;
  setTimeout(() => {
    if (pressed.value === k) pressed.value = "";
  }, 120);
}

/** Call: with nothing typed, first the last number called (shown, then
 * called on the second press), as the phones do */
async function call(): Promise<void> {
  if (!typed.value.trim()) {
    typed.value = S.lastDialed();
    return;
  }
  if (await S.dial(typed.value)) typed.value = "";
}

/** keys typed anywhere on the dialer page (no text field focused) */
export function dialerKey(e: KeyboardEvent): boolean {
  if (/^[0-9*#+]$/.test(e.key)) {
    typed.value += e.key;
    flash(e.key);
    return true;
  }
  if (e.key === "Backspace") {
    typed.value = typed.value.slice(0, -1);
    return true;
  }
  if (e.key === "Enter") {
    void call();
    return true;
  }
  if (e.key === "Escape") {
    typed.value = "";
    S.outgoing.value = ""; // the line chosen goes with the number
    return true;
  }
  return false;
}

/** "Réception (610) · 514 907-0060" */
function lineText(l: S.Line): string {
  const title = S.lineTitle(l.id);
  return l.callerId ? `${title} · ${S.prettyNumber(l.callerId)}` : title;
}

export function Dialer() {
  const many = S.lines.value.length > 1;
  const acc = S.outgoing.value || S.defaultAccount.value;
  const line = acc ? S.accounts.value[acc] : undefined;
  const matches = S.contactMatches(typed.value);
  const redial = !typed.value.trim() ? S.lastDialed() : "";
  return (
    <div class="page">
      <div class="dialer">
        <div class="dial-input">
          <input
            type="tel"
            id="dial-input"
            aria-label={t("dialer.placeholder")}
            placeholder={t("dialer.placeholder")}
            value={typed.value}
            autoComplete="off"
            onInput={(e) => (typed.value = (e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void call();
              } else if (e.key === "Escape") {
                typed.value = "";
                S.outgoing.value = "";
              }
            }}
          />
          {typed.value && (
            <button type="button" class="icon-only" aria-label={t("dialer.erase")} title={t("dialer.erase")} onClick={() => (typed.value = typed.value.slice(0, -1))}>
              <Icon name="backspace" size={22} />
            </button>
          )}
        </div>
        {/* always there, one row: the keypad never moves as matches come and go */}
        <div class="suggestions" data-testid="suggestions">
          {matches.map((c) => (
            <button type="button" class="suggestion" title={`${t("dialer.call")} ${c.name}`} onClick={() => void S.dial(c.number).then((ok) => ok && (typed.value = ""))}>
              <Icon name="phone" size={14} />
              <span class="grow">
                <span class="title">{c.name}</span>
                <span class="sub">{[c.company, S.prettyNumber(c.number)].filter(Boolean).join(" · ")}</span>
              </span>
            </button>
          ))}
        </div>
        <div class="keypad" role="group" aria-label={t("nav.keypad")}>
          {KEYS.map(([k, sub]) => (
            <button type="button" class={`key ${pressed.value === k ? "down" : ""}`} aria-label={k} onClick={() => (typed.value += k)}>
              <b>{k}</b>
              <small>{sub}</small>
            </button>
          ))}
        </div>
        <button
          type="button"
          class="call-btn"
          data-testid="call-btn"
          aria-label={redial ? t("dialer.redial", S.prettyNumber(redial)) : t("dialer.call")}
          title={redial ? t("dialer.redial", S.prettyNumber(redial)) : t("dialer.call")}
          disabled={!typed.value.trim() && !redial}
          onClick={() => void call()}
        >
          <Icon name="phone" size={30} />
        </button>
        <div class="dial-line">
          {many ? (
            <label class="line-pick">
              <span>{t("dialer.from")}</span>
              <select
                data-testid="line-pick"
                value={acc}
                onChange={(e) => {
                  const v = (e.target as HTMLSelectElement).value;
                  S.outgoing.value = v === S.defaultAccount.value ? "" : v;
                }}
              >
                {S.lines.value.map((l) => (
                  <option value={l.id}>
                    {lineText(l)}
                    {S.accounts.value[l.id]?.state === "registered" ? "" : ` (${t("status.offline").toLowerCase()})`}
                  </option>
                ))}
              </select>
            </label>
          ) : line ? (
            `${t("dialer.line", S.lineTitle(acc))} · ${line.state === "registered" ? t("status.online") : t("status.offline")}`
          ) : (
            t("dialer.no_line")
          )}
          <br />
          <span>{t("dialer.hint")}</span>
        </div>
      </div>
    </div>
  );
}
